import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { useGridApiContext } from '@mui/x-data-grid';
import { unwrapPrivateAPI } from '@mui/x-data-grid/internals';

const STEP_PX = 280;

/**
 * Scroll the grid body (and synced column headers) left/right when many columns overflow.
 */
export default function DataGridToolbarScrollNudge() {
  const apiRef = useGridApiContext();
  const theme = useTheme();
  const rtl = theme.direction === 'rtl';

  const red = theme.palette.error.main;
  const redDark = theme.palette.error.dark;
  const fg = theme.palette.getContrastText(red);

  const nudge = (deltaLogical) => {
    const api = apiRef.current;
    if (!api) return;

    const delta = rtl ? -deltaLogical : deltaLogical;
    const privateApi = unwrapPrivateAPI(api);
    const el = privateApi?.virtualScrollerRef?.current;

    if (el) {
      el.scrollBy({ left: delta, behavior: 'smooth' });
      return;
    }

    if (typeof api.getScrollPosition === 'function' && typeof api.scroll === 'function') {
      const { left, top } = api.getScrollPosition();
      api.scroll({ left: left + delta, top });
    }
  };

  const btnSx = {
    borderRadius: 0,
    py: 0.5,
    px: 0.75,
    color: fg,
    bgcolor: alpha(theme.palette.common.black, 0.12),
    '&:hover': {
      bgcolor: alpha(theme.palette.common.black, 0.22),
    },
  };

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'stretch',
        verticalAlign: 'middle',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: `0 2px 10px ${alpha(red, 0.45)}`,
        border: '2px solid',
        borderColor: alpha(redDark, 0.9),
        background: `linear-gradient(145deg, ${alpha(red, 0.95)}, ${alpha(redDark, 0.98)})`,
      }}
    >
      <Tooltip title={rtl ? 'Scroll grid toward end' : 'Scroll grid left'} placement="bottom">
        <IconButton
          size="small"
          onClick={() => nudge(-STEP_PX)}
          aria-label="Scroll grid left"
          sx={btnSx}
        >
          <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 26 }} />
        </IconButton>
      </Tooltip>
      <Box
        sx={{
          alignSelf: 'stretch',
          width: 1,
          bgcolor: alpha(fg, 0.25),
          my: 0.65,
        }}
      />
      <Tooltip title={rtl ? 'Scroll grid toward start' : 'Scroll grid right'} placement="bottom">
        <IconButton
          size="small"
          onClick={() => nudge(STEP_PX)}
          aria-label="Scroll grid right"
          sx={btnSx}
        >
          <KeyboardDoubleArrowRightIcon sx={{ fontSize: 26 }} />
        </IconButton>
      </Tooltip>
      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          px: 1.15,
          bgcolor: alpha(theme.palette.common.black, 0.18),
          borderLeft: `1px solid ${alpha(fg, 0.25)}`,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            letterSpacing: 0.4,
            whiteSpace: 'nowrap',
            color: fg,
            fontFamily: 'inherit',
            fontSize: '0.74rem',
          }}
        >
          Slide The Panel
        </Typography>
      </Box>
    </Box>
  );
}
